export const DEFAULT_BOND_WHEEL_CONSTANTS = {
  currency: "COIN",
  minEntryAmount: 500,
  maxTicketsPerJoin: 100,
  minDeadlineMinutes: 30,
  lockBeforeDeadlineMinutes: 5,
  minActiveParticipants: 2,
  minNumberOfWinners: 1,
  maxNumberOfWinners: 10,
  minMinimumPlayers: 2,
  maxMinimumPlayers: 100,
  platformFeeRate: 0.05,
  creatorFeeRate: 0.02,
};

export const OPEN_GAME_STATUSES = ["Active", "Locked"];
export const WHEEL_ENTRY_STATUSES = ["Active", "Won", "Lost"];
export const SETTLED_GAME_LOOKBACK_MS = 24 * 60 * 60 * 1000;

export const SLICE_COLORS = [
  "#1E88E5", "#26C6DA", "#66BB6A", "#FFCA28", "#FF7043", "#EF5350",
  "#EC407A", "#AB47BC", "#7E57C2", "#5C6BC0", "#42A5F5", "#26A69A",
  "#9CCC65", "#FFEE58", "#FFA726", "#8D6E63",
];

export const sliceColorForIndex = (index) =>
  SLICE_COLORS[((index % SLICE_COLORS.length) + SLICE_COLORS.length) % SLICE_COLORS.length];

export const formatCoin = (value) => {
  const num = Math.floor(Number(value) || 0);
  return num.toLocaleString("en-US");
};

export const pad2 = (value) => String(Math.max(0, value)).padStart(2, "0");

export function getCountdownParts(deadline, now = Date.now()) {
  const target = new Date(deadline).getTime();
  if (!Number.isFinite(target)) {
    return { expired: true, days: 0, hours: 0, minutes: 0, seconds: 0 };
  }
  const diff = target - now;
  if (diff <= 0) {
    return { expired: true, days: 0, hours: 0, minutes: 0, seconds: 0 };
  }
  const totalSec = Math.floor(diff / 1000);
  return {
    expired: false,
    days: Math.floor(totalSec / 86400),
    hours: Math.floor((totalSec % 86400) / 3600),
    minutes: Math.floor((totalSec % 3600) / 60),
    seconds: totalSec % 60,
  };
}

export function formatCountdownCompact(deadline, now = Date.now()) {
  const parts = getCountdownParts(deadline, now);
  if (parts.expired) return "00:00:00";
  const time = `${pad2(parts.hours)}:${pad2(parts.minutes)}:${pad2(parts.seconds)}`;
  if (parts.days > 0) return `${parts.days}d ${time}`;
  return time;
}

export function getGameId(game) {
  return game?._id ? String(game._id) : null;
}

export function getCreatorId(game) {
  const creator = game?.creator;
  if (!creator) return null;
  return typeof creator === "object" ? String(creator._id || creator.id || "") : String(creator);
}

export function isUserCreator(game, userId) {
  if (!game || !userId) return false;
  return getCreatorId(game) === String(userId);
}

export function getEntryUserId(entry) {
  if (!entry?.user) return "";
  return typeof entry.user === "object"
    ? String(entry.user?._id || entry.user?.id || "")
    : String(entry.user || "");
}

export function findUserEntry(game, userId) {
  if (!game?.entries || !userId) return null;
  return game.entries.find(
    (entry) => getEntryUserId(entry) === String(userId) && entry.status === "Active"
  );
}

export function findUserParticipationEntry(game, userId) {
  if (!game?.entries || !userId) return null;
  return game.entries.find(
    (entry) => getEntryUserId(entry) === String(userId) && WHEEL_ENTRY_STATUSES.includes(entry.status)
  );
}

export function isUserInGame(game, userId) {
  return !!findUserEntry(game, userId);
}

export function wasUserInGame(game, userId) {
  return !!findUserParticipationEntry(game, userId) || isUserCreator(game, userId);
}

export function getEntryTicketCount(entry) {
  const count = Math.floor(Number(entry?.ticketCount) || 1);
  return Number.isFinite(count) && count >= 1 ? count : 1;
}

export function getUserTicketCount(game, userId) {
  if (!game?.entries || !userId) return 0;
  const uid = String(userId);
  return game.entries
    .filter((entry) => getEntryUserId(entry) === uid && WHEEL_ENTRY_STATUSES.includes(entry.status))
    .reduce((sum, entry) => sum + getEntryTicketCount(entry), 0);
}

export function getGameWheelEntries(game) {
  const unitAmount = Math.floor(Number(game?.entryAmount) || 0);
  return (game?.entries || [])
    .filter((entry) => WHEEL_ENTRY_STATUSES.includes(entry.status))
    .flatMap((entry) => {
      const ticketCount = getEntryTicketCount(entry);
      const winningTickets = Array.isArray(entry.winningTickets) ? entry.winningTickets : [];
      const winningByTicket = new Map(
        winningTickets.map((ticket) => [Number(ticket.ticketNumber), ticket])
      );

      return Array.from({ length: ticketCount }, (_, idx) => {
        const ticketNumber = idx + 1;
        const winningTicket = winningByTicket.get(ticketNumber);
        const status =
          winningTickets.length > 0
            ? winningTicket
              ? "Won"
              : "Lost"
            : entry.status;

        return {
          ...entry,
          _id: `${entry._id}:ticket:${ticketNumber}`,
          sourceEntryId: entry._id,
          ticketNumber,
          ticketCount: 1,
          entryAmount: unitAmount || Math.floor(Number(entry.entryAmount || 0) / ticketCount),
          status,
          sliceIndex: winningTicket?.sliceIndex ?? (status === "Won" ? entry.sliceIndex : null),
          netPayout: winningTicket?.netPayout ?? (ticketCount === 1 ? entry.netPayout : 0),
          platformFee: winningTicket?.platformFee ?? 0,
          creatorFee: winningTicket?.creatorFee ?? 0,
        };
      });
    });
}

export function getGameNumberOfWinners(game, constants = DEFAULT_BOND_WHEEL_CONSTANTS) {
  const n = Number(game?.numberOfWinners);
  if (Number.isFinite(n) && n >= constants.minNumberOfWinners) {
    return Math.min(Math.floor(n), constants.maxNumberOfWinners);
  }
  return constants.minNumberOfWinners;
}

export function getGameMinimumPlayers(game, constants = DEFAULT_BOND_WHEEL_CONSTANTS) {
  const n = Number(game?.minimumPlayers);
  if (Number.isFinite(n) && n >= constants.minMinimumPlayers) {
    return Math.min(Math.floor(n), constants.maxMinimumPlayers);
  }
  return constants.minActiveParticipants || constants.minMinimumPlayers;
}

export function getGameWinnerEntries(game) {
  return (game?.entries || []).filter((entry) => entry.status === "Won");
}

export function getGameWinnerName(game) {
  const winners = getGameWinnerEntries(game);
  if (winners.length === 1 && winners[0]?.user?.name) return winners[0].user.name;
  if (winners.length > 1) {
    return winners.map((entry) => entry.user?.name || "Player").join(", ");
  }
  if (typeof game?.winner === "object" && game.winner?.name) return game.winner.name;
  return null;
}

export function getGameWinningSliceIndices(game) {
  if (Array.isArray(game?.winningSliceIndices) && game.winningSliceIndices.length > 0) {
    return game.winningSliceIndices.filter((idx) => Number.isFinite(idx));
  }
  if (Number.isFinite(game?.winningSliceIndex)) {
    return [game.winningSliceIndex];
  }
  return [];
}

export function isUserWinner(game, userId) {
  if (!game || !userId) return false;
  const uid = String(userId);
  return getGameWinnerEntries(game).some((entry) => getEntryUserId(entry) === uid);
}

export function getUserWinnerEntry(game, userId) {
  if (!game || !userId) return null;
  const uid = String(userId);
  return getGameWinnerEntries(game).find((entry) => getEntryUserId(entry) === uid) || null;
}

export function getRequiredMinimumPlayers(numberOfWinners, constants = DEFAULT_BOND_WHEEL_CONSTANTS) {
  const winners = Math.max(1, Math.floor(Number(numberOfWinners) || 1));
  const globalFloor = constants?.minMinimumPlayers || constants?.minActiveParticipants || 2;
  return Math.max(globalFloor, winners * 2 + 1);
}

export function isValidMinimumPlayersForWinners(numberOfWinners, minimumPlayers) {
  const winners = Math.max(1, Math.floor(Number(numberOfWinners) || 1));
  const minPlayers = Math.floor(Number(minimumPlayers) || 0);
  return minPlayers > winners * 2;
}

export function estimateMultiWinnerSharePayout(
  totalPot,
  winnerStake,
  numberOfWinners = 1,
  constants = DEFAULT_BOND_WHEEL_CONSTANTS
) {
  const pot = Math.floor(Number(totalPot) || 0);
  const stake = Math.floor(Number(winnerStake) || 0);
  const winners = Math.max(1, Math.floor(Number(numberOfWinners) || 1));
  const grossShare = Math.floor(pot / winners);
  const profit = Math.max(0, grossShare - stake);
  const platformFee = Math.floor(profit * constants.platformFeeRate);
  const creatorFee = Math.floor(profit * constants.creatorFeeRate);
  return {
    grossShare,
    profit,
    platformFee,
    creatorFee,
    netPayout: grossShare - platformFee - creatorFee,
    numberOfWinners: winners,
  };
}

export function getSettledWheelRotation(winningSliceIndex, participantCount) {
  if (!Number.isFinite(winningSliceIndex)) return null;
  const sliceCount = participantCount <= 1 ? 2 : participantCount;
  const anglePer = 360 / sliceCount;
  const centerAngle = winningSliceIndex * anglePer + anglePer / 2;
  return -centerAngle;
}

export function isRecentSettledGame(game, now = Date.now(), lookbackMs = SETTLED_GAME_LOOKBACK_MS) {
  if (!game || game.status !== "Settled") return false;
  const settledAt = new Date(game.settledAt || game.deadline).getTime();
  if (!Number.isFinite(settledAt)) return false;
  return now - settledAt <= lookbackMs;
}

export function mergeGamesInList(games, incoming = []) {
  return (incoming || []).reduce((acc, game) => upsertGameInList(acc, game), games || []);
}

export function makeJoinIdempotencyKey(userId, gameId) {
  return `bw-join-${userId}-${gameId}-${Date.now()}`;
}

export function computeLockAt(deadline, lockBeforeMinutes = 5) {
  const d = new Date(deadline);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getTime() - lockBeforeMinutes * 60 * 1000);
}

export function canJoinOrLeaveGame(game, now = Date.now(), constants = DEFAULT_BOND_WHEEL_CONSTANTS) {
  if (!game || game.status !== "Active") return false;
  const lockAt = new Date(game.lockAt || computeLockAt(game.deadline, constants.lockBeforeDeadlineMinutes));
  if (Number.isNaN(lockAt.getTime())) return false;
  return now < lockAt.getTime();
}

export function canJoinGame(game, userId, now = Date.now(), constants = DEFAULT_BOND_WHEEL_CONSTANTS) {
  if (!userId) return false;
  return canJoinOrLeaveGame(game, now, constants);
}

export function canLeaveGame(game, userId, now = Date.now(), constants = DEFAULT_BOND_WHEEL_CONSTANTS) {
  const entry = findUserEntry(game, userId);
  if (!entry || entry.isCreator) return false;
  return canJoinOrLeaveGame(game, now, constants);
}

export function upsertGameInList(games, game) {
  const id = getGameId(game);
  if (!id) return games;
  const index = games.findIndex((g) => getGameId(g) === id);
  if (index === -1) return [game, ...games];
  const next = [...games];
  next[index] = { ...next[index], ...game };
  return next;
}

export function getInitials(name = "") {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}
